<template>
  <div class="print-setting-general">
    <validation-observer
      class="validation-observer"
      tag="div"
      v-slot="{ invalid }"
    >
      <!-- 全般 -->
      <div class="section">
        <div class="heading">書類全般のデフォルト設定</div>
        <div class="form">
          <div class="form-item">
            <div class="label">用紙サイズ</div>
            <div class="input-field">
              <div class="radio-buttons size">
                <base-radio-button
                  v-for="option in radios.paperSize"
                  class="radio paper-size"
                  :data-test="['radio', 'paper-size', `${option.eachValue}`]"
                  :input-styles="radioButtonStyle"
                  :key="'paper-size-' + option.id"
                  :option="option"
                  v-model="printSetting.defaultPaperSize"
                />
              </div>
            </div>
          </div>
          <div class="form-item">
            <div class="label">医院ロゴ</div>
            <div class="input-field">
              <div
                class="image-wrap image"
                :class="{ border: !printSetting.image }"
              >
                <base-image-uploader
                  data-test="uploader image"
                  :image="printSetting.image"
                  :image-styles="{
                    height: '110px',
                    maxWidth: '300px',
                    objectFit: 'contain'
                  }"
                  :keyword="'image'"
                  :max-width-or-height="300"
                  :placeholder-styles="{
                    border: 'unset',
                    height: '110px',
                    width: '300px'
                  }"
                  @input="inputImage"
                  @delete="deleteImage"
                  @uploading-img="setUploadingImg"
                />
              </div>
            </div>
          </div>
          <div class="form-item">
            <div class="label">医院名</div>
            <div class="input-field">
              <div class="radio-buttons">
                <base-radio-button
                  v-for="option in radios.clinicName"
                  class="radio clinic-name"
                  :data-test="['radio', 'clinic-name', `${option.eachValue}`]"
                  :input-styles="radioButtonStyle"
                  :key="'clinic-' + option.id"
                  :option="option"
                  v-model="printSetting.printClinicNameFlg"
                />
              </div>
            </div>
          </div>
          <div class="form-item">
            <div class="label">医院電話番号</div>
            <div class="input-field">
              <div class="radio-buttons">
                <base-radio-button
                  v-for="option in radios.clinicTel"
                  class="radio clinic-tel"
                  :data-test="['radio', 'clinic-tel', `${option.eachValue}`]"
                  :input-styles="radioButtonStyle"
                  :key="'clinic-' + option.id"
                  :option="option"
                  v-model="printSetting.printClinicTelFlg"
                />
              </div>
            </div>
          </div>
          <div class="form-item">
            <div class="label">
              <span>医院メールアドレス</span>
              <div class="footnote-mark">※1</div>
            </div>
            <div class="input-field">
              <div class="radio-buttons">
                <base-radio-button
                  v-for="option in radios.clinicEmail"
                  class="radio clinic-email"
                  :data-test="['radio', 'clinic-email', `${option.eachValue}`]"
                  :input-styles="radioButtonStyle"
                  :key="'clinic-' + option.id"
                  :option="option"
                  v-model="printSetting.printClinicEmailFlg"
                />
              </div>
            </div>
          </div>
          <div class="form-item">
            <div class="label">医院住所</div>
            <div class="input-field">
              <div class="radio-buttons">
                <base-radio-button
                  v-for="option in radios.clinicAddress"
                  class="radio clinic-address"
                  :data-test="[
                    'radio',
                    'clinic-address',
                    `${option.eachValue}`
                  ]"
                  :input-styles="radioButtonStyle"
                  :key="'clinic-' + option.id"
                  :option="option"
                  v-model="printSetting.printClinicAddressFlg"
                />
              </div>
            </div>
          </div>
          <div class="form-item">
            <div class="label">印刷プレビュー</div>
            <div class="buttons">
              <base-button-border-orange
                data-test="preview-medical-payment general"
                :disabled="isButtonDisabled(invalid)"
                :styles="previewButtonStyle"
                @click="openPdfMedicalPayment"
                >診療明細印刷</base-button-border-orange
              >
              <base-button-border-orange
                data-test="preview-estimate"
                :disabled="isButtonDisabled(invalid)"
                :styles="previewButtonStyle"
                @click="openPdfEstimate"
                >見積書印刷</base-button-border-orange
              >
              <base-button-border-orange
                data-test="preview-prescription"
                :disabled="isButtonDisabled(invalid)"
                :styles="previewButtonStyle"
                @click="openPdfPrescription"
                >処方箋印刷</base-button-border-orange
              >
              <base-button-border-orange
                data-test="preview-examination"
                :disabled="isButtonDisabled(invalid)"
                :styles="previewButtonStyle"
                @click="openPdfExamination"
                >検査結果印刷</base-button-border-orange
              >
              <base-button-border-orange
                data-test="preview-vaccination-certificate general"
                :disabled="isButtonDisabled(invalid)"
                :styles="previewButtonStyle"
                @click="openPdfVaccinationCertificate"
                >予防接種証明書<br />印刷</base-button-border-orange
              >
              <base-button-border-orange
                data-test="preview-receipt"
                :disabled="isButtonDisabled(invalid)"
                :styles="previewButtonStyle"
                @click="openPdfReceipt(false)"
                >領収書印刷<br />（控え無し）</base-button-border-orange
              >
              <base-button-border-orange
                data-test="preview-receipt-copy"
                :disabled="isButtonDisabled(invalid)"
                :styles="previewButtonStyle"
                @click="openPdfReceipt(true)"
                >領収書印刷<br />（控え有り）</base-button-border-orange
              >
            </div>
          </div>
          <div class="footnote">
            <span
              >※1）予防接種証明書と領収書印刷では設定に関わらず表示されません。</span
            >
          </div>
        </div>
      </div>

      <!-- 診療明細 -->
      <div class="section">
        <div class="heading">診療明細のデフォルト設定</div>
        <div class="form">
          <div class="form-item">
            <div class="label">
              <span>処方の情報</span>
              <hint-balloon :note="notes.prescriptionFlg" />
            </div>
            <div class="input-field">
              <div class="radio-buttons">
                <base-radio-button
                  v-for="option in radios.prescription"
                  class="radio prescription"
                  :data-test="['radio', 'prescription', `${option.eachValue}`]"
                  :input-styles="radioButtonStyle"
                  :key="option.id"
                  :option="option"
                  v-model="printSetting.prescriptionFlg"
                />
              </div>
            </div>
          </div>
          <div class="form-item">
            <div class="label">
              <span>自己負担額のバーコード</span>
              <hint-balloon :note="notes.barcodeFlg" />
            </div>
            <div class="input-field">
              <div class="radio-buttons">
                <base-radio-button
                  v-for="option in radios.barcode"
                  class="radio barcode"
                  :data-test="['radio', 'barcode', `${option.eachValue}`]"
                  :input-styles="radioButtonStyle"
                  :key="option.id"
                  :option="option"
                  v-model="printSetting.barcodeFlg"
                />
              </div>
            </div>
          </div>
          <div class="form-item">
            <text-input-form
              data-test="input barcode-prefix"
              :body-style="{ width: '300px' }"
              :full-width-numbers-to-half-width-numbers="true"
              :heading-style="{ width: '260px' }"
              :maxlength="6"
              :note="notes.barcodePrefix"
              :required-flg="true"
              :styles="{ width: '120px' }"
              :validation-rules="{ numericString: { length: 6 } }"
              v-model="printSetting.barcodePrefix"
              >バーコードの上6桁</text-input-form
            >
          </div>
          <div class="form-item">
            <div class="label">印刷プレビュー</div>
            <div class="buttons">
              <base-button-border-orange
                data-test="preview-medical-payment"
                :disabled="isButtonDisabled(invalid)"
                :styles="previewButtonStyle"
                @click="openPdfMedicalPayment"
                >診療明細印刷</base-button-border-orange
              >
            </div>
          </div>
        </div>
      </div>

      <!-- 予防接種証明書 -->
      <div class="section">
        <div class="heading">予防接種証明書のデフォルト設定</div>
        <div class="form">
          <div class="form-item">
            <div class="label">医院印鑑</div>
            <div class="input-field">
              <div
                class="image-wrap stamp"
                :class="{ border: !printSetting.stamp }"
              >
                <base-image-uploader
                  data-test="uploader stamp"
                  :image="printSetting.stamp"
                  :keyword="'stamp'"
                  :max-width-or-height="300"
                  :placeholder-styles="{
                    width: '300px',
                    height: '110px',
                    border: 'unset'
                  }"
                  :image-styles="{
                    maxWidth: '300px',
                    height: '110px',
                    objectFit: 'contain'
                  }"
                  @input="inputStamp"
                  @delete="deleteStamp"
                  @uploading-img="setUploadingImg"
                />
              </div>
            </div>
          </div>
          <div class="form-item">
            <div class="label">印刷プレビュー</div>
            <div class="buttons">
              <base-button-border-orange
                data-test="preview-vaccination-certificate"
                :disabled="isButtonDisabled(invalid)"
                :styles="previewButtonStyle"
                @click="openPdfVaccinationCertificate"
                >予防接種証明書<br />印刷</base-button-border-orange
              >
            </div>
          </div>
        </div>
      </div>

      <div class="registration">
        <base-button-medium-orange
          v-if="!lookOnlyFlg"
          data-test="registration-button"
          :disabled="isButtonDisabled(invalid)"
          @click="updateData"
          >登録</base-button-medium-orange
        >
      </div>
    </validation-observer>
    <announce-popup
      v-if="resultPopup.isOpened"
      data-test="result-popup"
      v-bind="resultPopup"
      @close="resultPopup.close"
      >{{ resultPopup.message }}</announce-popup
    >
    <unsaved-leave-popup />
  </div>
</template>

<script>
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import BaseButtonBorderOrange from '@/components/parts/atoms/BaseButtonBorderOrange'
import BaseButtonMediumOrange from '@/components/parts/atoms/BaseButtonMediumOrange'
import BaseImageUploader from '@/components/parts/atoms/BaseImageUploader'
import BaseRadioButton from '@/components/parts/atoms/BaseRadioButton'
import CheckInputDifference from '@/components/mixins/CheckInputDifference'
import HintBalloon from '@/components/parts/atoms/HintBalloon'
import TextInputForm from '@/components/parts/molecules/TextInputForm'
import UnsavedLeavePopup from '@/components/popups/UnsavedLeavePopup'
import _ from 'lodash'
import dedent from 'dedent'
import moment from 'moment'
import { ValidationObserver } from 'vee-validate'
import { calcAge, makeBirthdayJp } from '@/utils/patient_info'
import { mapGetters } from 'vuex'
import { printEstimate } from '@/utils/print_estimate'
import { printExamination } from '@/utils/print_examination'
import { printMedicalPayment } from '@/utils/print_medical_payment'
import { printPrescription } from '@/utils/print_prescription'
import { printReceipt } from '@/utils/print_receipt'
import { printVaccinationCertificate } from '@/utils/print_vaccination_certificate'

const today = moment().format('Y年M月D日')

export default {
  name: 'PrintSettingGeneral',

  components: {
    AnnouncePopup,
    BaseButtonBorderOrange,
    BaseButtonMediumOrange,
    BaseImageUploader,
    BaseRadioButton,
    HintBalloon,
    TextInputForm,
    UnsavedLeavePopup,
    ValidationObserver
  },

  mixins: [CheckInputDifference],

  data() {
    return {
      inputAttributes: [
        'prescriptionFlg',
        'barcodeFlg',
        'barcodePrefix',
        'defaultPaperSize',
        'image',
        'printClinicAddressFlg',
        'printClinicEmailFlg',
        'printClinicNameFlg',
        'printClinicTelFlg',
        'stamp'
      ],
      notes: {
        prescriptionFlg: dedent`
          処方箋として出している処方情報を診療明細の薬剤名の後ろに続けて印刷します。
        `,
        barcodeFlg: dedent`
          診療明細書の印刷では、飼主の自己負担額をバーコード形式でも記載することが可能です。
          記載されるバーコードは13桁の数字からなります。

            1-6桁目：自由な数字を設定可能
            7-12桁目：飼主の自己負担額
            13桁目：チェックデジット

          飼主の自己負担額とチェックデジットの値は計算により自動で設定されます。
          ※金額が7桁以上になる場合はバーコードを利用できません。
        `,
        barcodePrefix: dedent`
          飼主の自己負担額のバーコードで使用される値です。
        `
      },
      previewButtonStyle: { width: '120px', height: '44px' },
      previews: {
        estimate: {
          insuranceFlg: 0,
          treatmentItems: [],
          diseaseName: 'プレビュー診断',
          date: today,
          discountPrice: 0,
          discountRate: 0,
          pledgeRate: 0
        },
        examination: {
          speciesName: 'プレビュー種別',
          treatmentName: 'プレビュー検査',
          resultTopicClasses: [
            {
              topicClassName: 'プレビュー項目区分',
              resultTopics: [
                {
                  topicName: 'プレビュー項目1',
                  result: '10',
                  unit: 'mg/dL',
                  reference: '0 – 40',
                  comment: 'プレビューコメント'
                },
                {
                  topicName: 'プレビュー項目2',
                  result: '0',
                  unit: '°C',
                  reference: '',
                  comment: ''
                }
              ]
            }
          ]
        },
        medicalPayment: {
          pledgeRate: 0,
          diseaseName: 'プレビュー診断',
          onsetDate: today,
          date: today,
          staffName: 'プレビュースタッフ',
          treatmentItems: [
            {
              name: 'プレビュー診療項目1',
              prescription: '',
              insuranceFlg: 0,
              taxExemptFlg: 0,
              unitPrice: 0,
              amount: 0,
              subtotal: 0
            },
            {
              name: 'プレビュー薬剤物品1',
              prescription: 'プレビュー処方の情報1',
              insuranceFlg: 0,
              taxExemptFlg: 0,
              unitPrice: 0,
              amount: 0,
              subtotal: 0
            },
            {
              name: 'プレビュー診療項目2',
              prescription: '',
              insuranceFlg: 0,
              taxExemptFlg: 0,
              unitPrice: 0,
              amount: 0,
              subtotal: 0
            }
          ],
          discountPrice: 0,
          discountRate: 0,
          burdenAmount: 1000
        },
        owner: {
          id: 1,
          lastName: 'プレビュー',
          firstName: '飼主',
          prefectureId: 13,
          postalCode: '1000000',
          address: '〇〇〇市〇〇〇町〇〇〇丁目',
          building: 'プレビュービル〇〇〇号室'
        },
        patient: {
          name: 'プレビュー患者',
          showId: '1-1',
          birthday: moment()
            .add(-15, 'M')
            .format('YYYYMMDD'),
          speciesName: 'プレビュー種別',
          breed: 'プレビュー品種',
          sexName: '♂',
          image: 'preview'
        },
        prescriptionItems: [
          {
            medicineName: 'プレビュー薬剤物品',
            prescription: 'プレビュー処方箋'
          }
        ]
      },
      printSetting: {},
      radioButtonStyle: { paddingLeft: '20px' },
      radios: {
        prescription: [
          { id: 'prescription-1', eachValue: 1, labelName: '表示する' },
          { id: 'prescription-0', eachValue: 0, labelName: '表示しない' }
        ],
        barcode: [
          { id: 'barcode-1', eachValue: 1, labelName: '表示する' },
          { id: 'barcode-0', eachValue: 0, labelName: '表示しない' }
        ],
        clinicAddress: [
          { id: 'address-1', eachValue: 1, labelName: '表示する' },
          { id: 'address-0', eachValue: 0, labelName: '表示しない' }
        ],
        clinicEmail: [
          { id: 'email-1', eachValue: 1, labelName: '表示する' },
          { id: 'email-0', eachValue: 0, labelName: '表示しない' }
        ],
        clinicName: [
          { id: 'name-1', eachValue: 1, labelName: '表示する' },
          { id: 'name-0', eachValue: 0, labelName: '表示しない' }
        ],
        clinicTel: [
          { id: 'tel-1', eachValue: 1, labelName: '表示する' },
          { id: 'tel-0', eachValue: 0, labelName: '表示しない' }
        ],
        paperSize: [
          { id: 'A4', eachValue: 'A4', labelName: 'A4' },
          { id: 'B5', eachValue: 'B5', labelName: 'B5' },
          { id: 'A5', eachValue: 'A5', labelName: 'A5' }
        ]
      },
      resultPopup: {
        buttons: ['閉じる'],
        close: this.closeResultPopup,
        isOpened: false,
        message: '',
        title: '',
        type: ''
      },
      updating: false,
      uploadingImg: false
    }
  },

  computed: {
    ...mapGetters({
      clinic: 'clinic/getData',
      getMasterDatum: 'master/getDataById',
      lookOnlyFlg: 'auth/lookOnlyFlg',
      prefectures: 'master/getPrefectures'
    })
  },

  created() {
    this.setData()
  },

  methods: {
    closeResultPopup() {
      this.resultPopup.isOpened = false
    },
    deleteImage() {
      this.printSetting.image = null
    },
    deleteStamp() {
      this.printSetting.stamp = null
    },
    inputImage(imageData) {
      this.printSetting.image = imageData
    },
    inputStamp(stampData) {
      this.printSetting.stamp = stampData
    },
    isButtonDisabled(invalid) {
      return invalid || this.updating || this.uploadingImg
    },
    makePrintOptions() {
      const printOptions = {
        ownerName: 'プレビュー飼主',
        paperSize: this.printSetting.defaultPaperSize,
        proviso: '診療代',
        receiptDate: new Date(),
        showBarcode: !!this.printSetting.barcodeFlg,
        showClinicAddress: !!this.printSetting.printClinicAddressFlg,
        showClinicEmail: !!this.printSetting.printClinicEmailFlg,
        showClinicName: !!this.printSetting.printClinicNameFlg,
        showClinicTel: !!this.printSetting.printClinicTelFlg,
        showInvoiceNumber: !!this.clinic.invoiceNumber,
        showPrescription: !!this.printSetting.prescriptionFlg
      }
      return printOptions
    },
    makeSignatureItems() {
      const prefecture = this.getMasterDatum(
        'prefectures',
        this.clinic.prefectureId
      )
      const prefectureName = prefecture?.name ?? ''
      const clinic = { ...this.clinic, prefectureName }
      const { image: clinicImage, stamp: clinicStamp } = this.printSetting
      return { clinic, clinicImage, clinicStamp }
    },
    openPdfEstimate() {
      const printOptions = this.makePrintOptions()
      const signatureItems = this.makeSignatureItems()
      printEstimate({
        printOptions,
        clinic: signatureItems.clinic,
        clinicImage: signatureItems.clinicImage,
        owner: this.previews.owner,
        patient: this.previews.patient,
        estimate: this.previews.estimate
      })
    },
    openPdfExamination() {
      const printOptions = this.makePrintOptions()
      const signatureItems = this.makeSignatureItems()
      printExamination({
        printOptions,
        clinic: signatureItems.clinic,
        clinicImage: signatureItems.clinicImage,
        owner: this.previews.owner,
        patient: this.previews.patient,
        examination: this.previews.examination,
        examinationDate: today
      })
    },
    openPdfMedicalPayment() {
      const printOptions = this.makePrintOptions()
      const signatureItems = this.makeSignatureItems()
      printMedicalPayment({
        printOptions,
        clinic: signatureItems.clinic,
        clinicImage: signatureItems.clinicImage,
        barcodePrefix: this.printSetting.barcodePrefix,
        printData: [
          {
            owner: this.previews.owner,
            patient: this.previews.patient,
            medicalPayment: this.previews.medicalPayment
          }
        ]
      })
    },
    openPdfPrescription() {
      const printOptions = this.makePrintOptions()
      const signatureItems = this.makeSignatureItems()
      printPrescription({
        printOptions,
        clinic: signatureItems.clinic,
        clinicImage: signatureItems.clinicImage,
        owner: this.previews.owner,
        patient: this.previews.patient,
        treatmentDate: today,
        prescriptionItems: this.previews.prescriptionItems
      })
    },
    openPdfReceipt(isCopyRequired) {
      const printOptions = this.makePrintOptions()
      const signatureItems = this.makeSignatureItems()
      printReceipt({
        printOptions: { ...printOptions, isCopyRequired },
        clinic: signatureItems.clinic,
        clinicImage: signatureItems.clinicImage,
        payment: { burdenAmount: 1000 }
      })
    },
    openPdfVaccinationCertificate() {
      const printOptions = this.makePrintOptions()
      const signatureItems = this.makeSignatureItems()
      const displayBirthday = makeBirthdayJp(this.previews.patient)
      const age = calcAge(this.previews.patient)
      const prefecture = this.getMasterDatum(
        'prefectures',
        this.previews.owner.prefectureId
      )
      const prefectureName = prefecture?.name ?? ''
      printVaccinationCertificate({
        printOptions,
        clinic: signatureItems.clinic,
        clinicImage: signatureItems.clinicImage,
        clinicStamp: signatureItems.clinicStamp,
        owner: { ...this.previews.owner, prefectureName },
        patient: { ...this.previews.patient, displayBirthday, age },
        vaccineData: {
          name: 'プレビューワクチン',
          date: today,
          nextDate: moment()
            .add(1, 'Y')
            .format('Y年M月D日'),
          staffName: 'プレビュースタッフ',
          lotNo: '1234',
          memo: ''
        }
      })
    },
    setData() {
      this.printSetting = { ...this.$store.getters['printSetting/getData'] }
      this.mixinInputData = this.printSetting
      this.mixinSetInitialData()
    },
    setUploadingImg(uploadingImgState) {
      this.uploadingImg = uploadingImgState
    },
    async updateData() {
      this.updating = true
      const res = await this.$store.dispatch(
        'printSetting/update',
        this.printSetting
      )
      if (res === true) {
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '編集しました',
          title: '完了',
          type: 'success'
        }
        this.setData()
      } else {
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '編集に失敗しました',
          title: '失敗',
          type: 'failure'
        }
      }
      this.updating = false
    }
  }
}
</script>

<style lang="scss" scoped>
$input-line-height: 33px;
.print-setting-general {
  margin-top: 22px;
  padding-bottom: 100px;
  text-align: left;
  font-size: 15px;
  > .validation-observer {
    display: flex;
    flex-direction: column;
    row-gap: 70px;
    > .section {
      > .heading {
        padding-bottom: 20px;
        padding-left: 6px;
        border-bottom: 1px solid #{$light-grey};
        font-weight: bold;
      }
      > .form {
        margin-top: 30px;
        padding-left: 20px;
        display: flex;
        flex-direction: column;
        row-gap: 25px;
        > .form-item {
          display: flex;
          align-items: flex-start;
          > .label {
            display: flex;
            align-items: center;
            width: 260px;
            min-height: $input-line-height;
            > .footnote-mark {
              margin-left: 6px;
              font-size: 12px;
            }
          }
          > .input-field {
            flex: 1;
            display: flex;
            align-items: center;
            min-height: $input-line-height;
            > .radio-buttons {
              display: flex;
              column-gap: 20px;
              &.size {
                column-gap: 10px;
              }
            }
            > .image-wrap {
              width: 300px;
              height: 140px;
              display: flex;
              align-items: center;
              box-sizing: border-box;
              &.border {
                border: 2px solid #{$pumpkin};
              }
            }
          }
          > .buttons {
            flex: 1;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            column-gap: 10px;
            row-gap: 10px;
            ::v-deep .base-button-border-orange {
              > button {
                &:active {
                  margin: 2px auto 0 auto;
                  max-height: 42px;
                }
              }
            }
          }
        }
        > .footnote {
          font-size: 12px;
        }
      }
    }
    > .registration {
      margin-top: 20px;
      display: flex;
      justify-content: center;
      max-width: 1200px;
    }
  }
}
</style>
